// SSO Service for handling SAML authentication with Cognito Hosted UI
export class SSOService {
    // Cognito configuration
    static getCognitoConfig() {
        return {
            authority: `https://cognito-idp.us-east-1.amazonaws.com/${process.env.REACT_APP_USER_POOL_ID}`,
            client_id: process.env.REACT_APP_USER_POOL_CLIENT_ID,
            redirect_uri: process.env.REACT_APP_REDIRECT_URI ?  process.env.REACT_APP_REDIRECT_URI  : "http://localhost:3000/",
            response_type: "code",
            scope: "email openid",
            // Derive the hosted UI domain from the authority
            domain: process.env.REACT_APP_COGNITO_DOMAIN
        };
    }

    static getCognitoHostedUIUrl() {
        const config = this.getCognitoConfig();
        const domain = config.domain;
        const clientId = config.client_id;
        const redirectUri = config.redirect_uri;
        
        // Validate required parameters
        if (!domain) {
            throw new Error('Cognito domain is not configured');
        }
        if (!clientId) {
            throw new Error('Cognito client ID is not configured');
        }
        if (!redirectUri) {
            throw new Error('Redirect URI is not configured');
        }

        // Validate domain format
        if (!domain.startsWith('https://')) {
            throw new Error('Cognito domain must start with https://');
        }

        // Generate a random state for security
        const state = this.generateState();
        
        const params = new URLSearchParams({
            response_type: config.response_type,
            client_id: clientId,
            redirect_uri: redirectUri,
            scope: config.scope,
            state: state
        });

        const ssoUrl = `${domain}/oauth2/authorize?${params.toString()}`;
        
        // Validate the final URL
        try {
            new URL(ssoUrl);
        } catch (error) {
            throw new Error(`Invalid SSO URL generated: ${error.message}`);
        }

        return ssoUrl;
    }

    static generateState() {
        const state = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
        sessionStorage.setItem('oauth_state', state);
        return state;
    }

    static validateState(returnedState) {
        const storedState = sessionStorage.getItem('oauth_state');
        sessionStorage.removeItem('oauth_state');
        return storedState === returnedState;
    }

    static async exchangeCodeForTokens(code) {
        const config = this.getCognitoConfig();
        const domain = config.domain;
        const clientId = config.client_id;
        const redirectUri = config.redirect_uri;

        const tokenEndpoint = `${domain}/oauth2/token`;
        
        const params = new URLSearchParams({
            grant_type: 'authorization_code',
            client_id: clientId,
            code: code,
            redirect_uri: redirectUri
        });

        try {
            console.log('Exchanging code for tokens...');
            console.log('Token endpoint:', tokenEndpoint);
            console.log('Client ID:', clientId);
            console.log('Redirect URI:', redirectUri);
            
            const response = await fetch(tokenEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: params.toString()
            });

            console.log('Token exchange response status:', response.status);
            console.log('Token exchange response headers:', response.headers);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Token exchange failed:', response.status, errorText);
                throw new Error(`Token exchange failed: ${response.status} - ${errorText}`);
            }

            const tokens = await response.json();
            console.log('Token exchange successful');
            return tokens;
        } catch (error) {
            console.error('Token exchange error:', error);
            throw error;
        }
    }

    static parseJWT(token) {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));
            return JSON.parse(jsonPayload);
        } catch (error) {
            console.error('JWT parsing error:', error);
            return null;
        }
    }

    static async getUserFromTokens(tokens) {
        const idTokenPayload = this.parseJWT(tokens.id_token);
        
        if (!idTokenPayload) {
            throw new Error('Invalid ID token');
        }

        // Create a user object that matches the expected format
        const userAttributes = {
            email: idTokenPayload.email,
            email_verified: idTokenPayload.email_verified,
            sub: idTokenPayload.sub,
            'cognito:username': idTokenPayload['cognito:username'] || idTokenPayload.sub
        };

        // Add any other attributes from the token
        Object.keys(idTokenPayload).forEach(key => {
            if (!userAttributes[key] && key !== 'aud' && key !== 'exp' && key !== 'iat' && key !== 'iss') {
                userAttributes[key] = idTokenPayload[key];
            }
        });

        return {
            user: null, // We don't have a CognitoUser object for SSO
            session: null,
            attributes: userAttributes,
            tokens: {
                accessToken: tokens.access_token,
                idToken: tokens.id_token,
                refreshToken: tokens.refresh_token
            },
            isSSO: true
        };
    }

    static redirectToSSO() {
        try {
            console.log('=== Starting SSO redirect ===');
            const config = this.getCognitoConfig();
            console.log('Cognito configuration:');
            console.log('Authority:', config.authority);
            console.log('Client ID:', config.client_id);
            console.log('Redirect URI:', config.redirect_uri);
            console.log('Domain:', config.domain);
            console.log('Scope:', config.scope);
            
            const ssoUrl = this.getCognitoHostedUIUrl();
            console.log('Generated SSO URL:', ssoUrl);
            
            // Test the URL before redirecting
            const testUrl = new URL(ssoUrl);
            console.log('URL validation passed:', testUrl.href);
            
            // Show redirect URI info
            console.log('✅ Using configured redirect URI:', config.redirect_uri);
            
            // Redirect to Cognito hosted UI
            console.log('Redirecting to:', ssoUrl);
            window.location.href = ssoUrl;
        } catch (error) {
            console.error('SSO redirect error:', error);
            alert(`SSO Error: ${error.message}`);
            throw error;
        }
    }

    static async handleCallback() {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');

        console.log('=== Handling OAuth callback ===');
        console.log('Code:', code ? 'Present' : 'Missing');
        console.log('State:', state);
        console.log('Error:', error);
        console.log('Error Description:', errorDescription);

        if (error) {
            const errorMsg = errorDescription ? `${error}: ${errorDescription}` : error;
            throw new Error(`SSO Error: ${errorMsg}`);
        }

        if (!code) {
            throw new Error('No authorization code received');
        }

        if (!this.validateState(state)) {
            throw new Error('Invalid state parameter - possible CSRF attack');
        }

        try {
            const tokens = await this.exchangeCodeForTokens(code);
            console.log('Successfully exchanged code for tokens');
            
            const user = await this.getUserFromTokens(tokens);
            console.log('Successfully parsed user from tokens:', user);
            
            return user;
        } catch (error) {
            console.error('Error in handleCallback:', error);
            throw error;
        }
    }

    // Helper method to check if we're in a callback
    static isCallback() {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.has('code') || urlParams.has('error');
    }

    // Helper method to clean up URL after callback
    static cleanupCallbackUrl() {
        window.history.replaceState({}, document.title, window.location.pathname);
    }
}